import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';

import { ListModel } from '@app/common/models/list.model';
import { AuthService } from '@app/common/services/auth.service';
import { DocumentModel } from '@app/common/models/document.model';
import { ComponentModel } from '@app/common/models/component.model';
import { DialogService } from '@app/common/services/dialog.service';
import { Restangular } from 'ngx-restangular';
import { ClassName } from '@app/common/enums/class-name.enum';
import { DocumentEditComponent } from '@app/ps/components/document-edit/document-edit.component';
import { ComponentEditComponent } from '@app/ps/components/component-edit/component-edit.component';
import { FileUtils } from '@app/common/utils/file.utils';
import { AlertComponent } from '@app/common/components/alert/alert.component';
import { SortModel } from '@app/common/models/sort.model';
import * as _ from 'lodash';
import { UserNamePipe } from '@app/common/pipes/username.pipe';
import { AttachmentTypePipe } from '@app/common/pipes/attachment-type.pipe';
import { ErrorHandlerService } from '@app/common/services/error-handler.service';
import { AttachmentTypeModel } from '@app/common/models/attachment-type.model';
import { UploadFileExtended } from '@app/common/components/fileupload/fileupload.component';
import { AttachmentChooseTypeOnlyFormComponent } from '@app/common/components/attachment-choose-type-only-form/attachment-choose-type-only-form.component';
import { DisabledAttachmentType } from '@app/common/components/attachment-choose-type-form/attachment-choose-type-form.component';
import { UploadService } from '@app/common/services/upload.service';
import { APP_CONFIG } from '@app/common/services/config.service';

@Component({
  selector: 'gmt-documents',
  templateUrl: './documents.component.html',
  styleUrls: ['./documents.component.scss']
})
export class DocumentsComponent implements OnInit, OnDestroy, OnChanges {

  public static readonly VIEWER_URL = window.location.pathname + `pdf-viewer/web/viewer.html?file=`;
  public static readonly DEFAULT_FILE_MSG = 'Vložte komponentu (soubor) přetažením, nebo vyberte z počítače.';

  @Input() documentList: ListModel<DocumentModel>;
  @Input() authorizedUser: boolean;
  @Input() folderId: number;
  @Input() showDocumentRelations = false;
  @Input() attachmentTypes: AttachmentTypeModel[];
  @Input() disabledAttachmentTypes: DisabledAttachmentType[];
  @Input() uploadDestination: any;
  @Input() mapEnabled = false;
  @Output() updated = new EventEmitter<boolean>();

  restBaseUrl: string;
  authToken: string;
  fileMsg = DocumentsComponent.DEFAULT_FILE_MSG;
  componentSortOrder: SortModel = { sortBy: 'timestamp', direction: 'asc' };
  documentSortOrder: SortModel = { sortBy: 'timestamp', direction: 'desc' };
  editInterval = null;
  uploadingFiles: UploadFileExtended[] = [];
  ENV = this.config.ENV;
  canEdit = false;
  user = this.authService.getUser();
  project = this.authService.getActualProject();

  constructor(
    private authService: AuthService,
    private dialogService: DialogService,
    private restangular: Restangular,
    private usernamePipe: UserNamePipe,
    private attachmentTypePipe: AttachmentTypePipe,
    private errorHandlerService: ErrorHandlerService,
    private uploadService: UploadService,
    @Inject(APP_CONFIG) private config,
  ) {
    this.getComponentSortFunction = this.getComponentSortFunction.bind(this);
    this.onFileSelect = this.onFileSelect.bind(this);
    this.onUpdateDocument = this.onUpdateDocument.bind(this);
    this.onUpdateComponent = this.onUpdateComponent.bind(this);
  }

  ngOnInit() {
    this.restBaseUrl = this.authService.getActiveApplicationRestUrl();
    if (this.restBaseUrl.indexOf('http://') !== 0 && this.restBaseUrl.indexOf('https://') !== 0) {
      if (this.restBaseUrl.indexOf('/') !== 0 ) {
        this.restBaseUrl = window.location.pathname + this.restBaseUrl;
      }
      this.restBaseUrl = window.location.protocol + '//' + window.location.host + this.restBaseUrl;
    }
    this.authToken = this.authService.getToken();

    // geodet muze editovat dokumenty pouze v detailu GP
    if (this.uploadDestination && this.uploadDestination.geometricPlanRequestId && this.authService.hasPermission('manage_geometric_plans')) {
      this.canEdit = true;
    } else {
      this.canEdit = this.authService.hasPermission('manipulate_files');
    }

    this.createEditInterval();
  }

  ngOnDestroy() {
    clearInterval(this.editInterval);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['documentList']) {
      this.documentSort();
      this.componentSort();
    }
  }

  private createEditInterval() {
    if (this.documentList.itemCount) {
      this.editInterval = setInterval(() => {
        this.restangular.one('documents/components/edited').get().toPromise().then((edited) => {
          this.documentList.list.forEach(d => {
            if (d.sourceType === 'MPV') {
              d.components.forEach(c => {
                const matched = edited.find(e => e.id === c.id);
                if ((matched && !c.editLock) || (!matched && c.editLock)) {
                  this.updated.emit();
                }
              });
            }
          });
        });
      }, 5000);
    }
  }

  getComponentUrl(component: ComponentModel) {
    return `${DocumentsComponent.VIEWER_URL}${this.restBaseUrl}/documents/${component.document.id}/components/${component.id}/content-pdf%3Ft%3D${this.authToken}`;
  }

  onCollapseAll(collapse: boolean) {
    this.documentList.list.forEach(d => d.collapsed = collapse);
  }

  canManipulateDocument(document: DocumentModel): boolean {
    const acquisitionDeeds = AttachmentTypeModel.getByCategory('acquisitionDeed').map(a => a.id);
    return !document.folder.businessCase
      || (document.folder.businessCase && !document.folder.businessCase.heliosImportStatus)
      || document.components.every(component => !acquisitionDeeds.includes(component.attachmentType));
  }

  onReturnDocument(document: DocumentModel) {
    this.dialogService.confirmDialogPromise('Opravdu chcete vrátit dokument do EPD?')
      .then((data) => {
        if (data === true) {
          this.restangular.one('documents', document.id).customPOST({}, 'return')
            .toPromise()
            .then(() => {
              this.updated.emit();
            }, this.errorHandlerService.get);
        }
      });
  }

  onUpdateDocument(document: DocumentModel) {
    const dialog = this.dialogService.open(DocumentEditComponent, {
      data: {
        document: document,
        canEdit: this.canEdit
      },
      className: ClassName.HIGHER_DIALOG,
    });

    const sub = dialog.afterClosed.subscribe((result: DocumentModel | boolean) => {
      if (result) {
        this.restangular.one('documents', document.id).customPOST(result, 'update')
          .toPromise()
          .then(() => {
            this.updated.emit();
          });
      }
      sub.unsubscribe();
    });
  }

  onUpdateComponent(component: ComponentModel, document: DocumentModel) {
    const acquisitionDeeds = AttachmentTypeModel.getByCategory('acquisitionDeed').map(a => a.id);

    const canManipulate = !document.folder
      || !document.folder.businessCase
      || (document.folder.businessCase && !document.folder.businessCase.heliosImportStatus)
      || !acquisitionDeeds.includes(component.attachmentType);

    const dialog = this.dialogService.open(ComponentEditComponent, {
      data: {
        component: component,
        canEdit: this.canEdit,
        canManipulate: canManipulate
      },
      className: ClassName.HIGHER_DIALOG,
    });

    const sub = dialog.afterClosed.subscribe((result: ComponentModel | boolean) => {
      if (result) {
        this.restangular.one('documents', document.id).one('components', component.id).customPOST(result, 'update')
          .toPromise()
          .then(() => {
            this.updated.emit();
          });
      }
      sub.unsubscribe();
    });
  }

  onEditComponent(component: ComponentModel) {
    return () => {
      return this.restangular.one('documents', component.document.id).one('components', component.id).customPOST(component, 'edit')
        .toPromise()
        .then(response => {
          if (response.success) {
            // window.open('ms-word:ofe|u|' + response.link, '_blank');
            window.open(response.link, '_blank');
            this.updated.emit();
          } else {
            this.dialogService.open(AlertComponent, {
              className: ClassName.ADJUSTED_DIALOG,
              data: {
                msg: response.errorMessage,
              },
            });
          }
        });
    };
  }

  onCancelComponent(component: ComponentModel) {
    this.dialogService.confirmDialogPromise('Opravdu chcete zrušit soubor?')
      .then((data) => {
        if (data === true) {
          this.restangular.one('documents', component.document.id).one('components', component.id).customPOST({}, 'cancel')
            .toPromise()
            .then(() => {
              this.updated.emit();
            }, this.errorHandlerService.get);
        }
      });
  }

  canManipulateComponent(document: DocumentModel, component: ComponentModel): boolean {
    if (component.editLock) {
      return false;
    }

    if ((component.sourceType !== 'MPV_GENERATED' && component.sourceType !== 'MPV_INSERTED') || !document.folder) {
      return false;
    }

    if (document.folder.geometricPlanRequest) {
      return ['APPROVED', 'REGISTER_REQUESTED', 'REGISTERED', 'IMPORT_REQUESTED'].includes(document.folder.geometricPlanRequest.status);
        /*&& ['GEOMETRIC_PLAN', 'GEOMETRIC_PLAN_VFK'].includes(component.attachmentType);*/
    }

    if (document.folder.opinionRequest) {
      return ['REALIZED'].includes(document.folder.opinionRequest.status)
        /* && ['OPINION_SCAN'].includes(component.attachmentType)*/;
    }

    return true;
  }

  fileType(component: ComponentModel, type: 'pdf' | 'word' | 'excel' | 'vfk' | ''): boolean {
    const extension = FileUtils.getFileExtension({ name: component.baseName });

    const isWord = extension === 'docx';
    if (type === 'word' && isWord) {
      return true;
    }

    const isExcel = extension === 'xlsx';
    if (type === 'excel' && isExcel) {
      return true;
    }

    const isPdf = extension === 'pdf';
    if (type === 'pdf' && isPdf) {
      return true;
    }

    const isVfk = extension === 'vfk';
    if (type === 'vfk' && isVfk) {
      return true;
    }

    return type === '' && !isWord && !isExcel && !isPdf;
  }

  onDocumentSort(sortBy: string) {
    this.documentSortOrder.direction = sortBy === this.documentSortOrder.sortBy
      ? this.documentSortOrder.direction === 'asc' ? 'desc' : 'asc'
      : 'asc';
    this.documentSortOrder.sortBy = sortBy;
    this.documentSort();
  }

  private documentSort() {
    this.documentList.list = _.orderBy(
      this.documentList.list,
      [((document: DocumentModel) => document.folder && document.folder.id === this.folderId ? 0 : 1), this.documentSortOrder.sortBy],
      ['asc', this.documentSortOrder.direction as 'asc' | 'desc']
    );
  }

  onComponentSort(sortBy: string) {
    this.componentSortOrder.direction = sortBy === this.componentSortOrder.sortBy
      ? this.componentSortOrder.direction === 'asc' ? 'desc' : 'asc'
      : 'asc';
    this.componentSortOrder.sortBy = sortBy;
    this.componentSort();
  }

  private componentSort() {
    this.documentList.list.forEach((item) => {
      item.components = _.orderBy(item.components, this.getComponentSortFunction(this.componentSortOrder.sortBy), this.componentSortOrder.direction as any);
    });
  }

  async onFileSelect(files: UploadFileExtended[]) {
    if (files.length === 1) {
      const dialog = this.dialogService.open(AttachmentChooseTypeOnlyFormComponent, {
        data: {
          attachmentTypes: this.attachmentTypes,
          disabledAttachmentTypes: this.disabledAttachmentTypes
        },
        className: ClassName.HIGHER_DIALOG,
      });

      const sub = dialog.afterClosed.subscribe((result: AttachmentTypeModel) => {
        if (result) {
          const sendData = {
            file: files[0].fileEntry,
            filename: files[0].fileEntry.name,
            attachmentType: result.id
          };

          this.uploadService.upload({
            url: this.authService.getActiveApplicationRestUrl() + '/documents/components/upload',
            data: {...sendData, ...this.uploadDestination},
            headers: { Authorization: this.authService.getToken() }
          }).progress(event => {
            files[0].progress = (100.0 * event.loaded / event.total);
          }).success(data => {
            this.clearFiles(files);
            this.updated.emit();
            this.uploadingFiles.splice(this.uploadingFiles.indexOf(files[0]), 1);
            this.updated.emit(true);
          }).error(() => {
            files[0].error = true;
          });
        } else {
          this.clearFiles(files);
        }

        sub.unsubscribe();
      });
    } else if (files.length > 1) {
      this.fileMsg = 'Je možné vložit pouze jeden soubor.';
    } else {
      this.clearFiles(files);
    }
  }

  private clearFiles(files: UploadFileExtended[]) {
    this.uploadingFiles.splice(0, this.uploadingFiles.length);
    files.splice(0, files.length);
    this.fileMsg = DocumentsComponent.DEFAULT_FILE_MSG;
  }

  private getComponentSortFunction(column) {
    return (value): any => {
      if (column === 'createdUser') {
        return this.usernamePipe.transform(value[column]);
      }

      if (column === 'attachmentType') {
        return this.attachmentTypePipe.transform({ type: value[column]}, 'title');
      }

      return value[column];
    };
  }
}
